import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Icon } from '../components/Icon';
import { Link } from 'react-router-dom';
import * as ep from '../api/endpoints';
import type { CategoryStat, ExpenseStats } from '../api/types';
import { usePeriods } from '../components/PeriodPicker';
import { Empty, ErrorBox, Loading, Money, PeriodBadge, ReceiptBadge } from '../components/ui';
import { currentYearMonth, formatDate, yearMonthLabel } from '../lib/dates';
import { apiToKurus } from '../lib/money';

const MONTHS_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

/**
 * Gider istatistiği: aylık ve yıllık. Sistem fişlerin kategorilerinden gider kalemlerini kendisi oluşturur;
 * benzer yazımlar ("Yemek", "yemekler") ve sık eşanlamlılar ("Benzin", "Akaryakıt" → Yakıt) tek kalemde toplanır.
 * Her kalemin yanında o dönemdeki toplam harcama yazar; kaleme dokununca o dönemdeki tüm fişleri açılır.
 * Önceki yılların verisi silinmez; yıl seçiciden geçmiş yıllar açılır.
 */
export function StatsScreen() {
  const [mode, setMode] = useState<'month' | 'year'>('month');
  const periods = usePeriods();
  const [ym, setYm] = useState<string | undefined>();
  const [year, setYear] = useState<string>(currentYearMonth().slice(0, 4));
  const months = periods.data?.map((p) => p.year_month) ?? [];
  const selectedYm = ym ?? months[0] ?? currentYearMonth();
  const scope = mode === 'month' ? { year_month: selectedYm } : { year };
  const q = useQuery({ queryKey: ['stats', scope], queryFn: () => ep.expenseStats(scope) });
  const s = q.data;

  return (
    <div className="stack">
      <div className="page-head">
        <h1>Gider istatistiği</h1>
        <span className="spacer" />
        <PdfButton scope={scope} label={mode === 'month' ? `${yearMonthLabel(selectedYm)} PDF` : `${year} yılı PDF`} />
      </div>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={mode === 'month'} className={mode === 'month' ? 'active' : ''} onClick={() => setMode('month')}>Aylık</button>
        <button role="tab" aria-selected={mode === 'year'} className={mode === 'year' ? 'active' : ''} onClick={() => setMode('year')}>Yıllık</button>
      </div>
      <div className="filters">
        {mode === 'month' ? (
          <label className="field"><span>Ay</span>
            <select value={selectedYm} onChange={(e) => setYm(e.target.value)}>
              {(months.length ? months : [selectedYm]).map((m) => <option key={m} value={m}>{yearMonthLabel(m)}</option>)}
            </select>
          </label>
        ) : (
          <label className="field"><span>Yıl</span>
            <select value={year} onChange={(e) => setYear(e.target.value)}>
              {(s?.years ?? [year]).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
        )}
      </div>
      <ErrorBox error={q.error} />
      {!s ? <Loading /> : (
        <>
          <Summary s={s} />
          {s.scope === 'year' && s.months && (
            <YearChart months={s.months} onPick={(m) => { setYm(m); setMode('month'); }} />
          )}
          <CategoryList s={s} />
        </>
      )}
    </div>
  );
}

function Summary({ s }: { s: ExpenseStats }) {
  return (
    <>
      <div className="row">
        <strong>{s.scope === 'month' && s.year_month ? yearMonthLabel(s.year_month) : `${s.year} yılı`}</strong>
        {s.scope === 'month' && s.period_status && <PeriodBadge status={s.period_status} />}
        <span className="small muted">{s.receipt_count} fiş · {s.categories.length} gider kalemi</span>
      </div>
      <div className="grid">
        <div className="card stat"><div className="label">Net gider</div><div className="value"><Money value={s.totals.net} /></div></div>
        <div className="card stat"><div className="label">Brüt gider</div><div className="value"><Money value={s.totals.gross} /></div></div>
        <div className="card stat"><div className="label">İade</div><div className="value"><Money value={s.totals.refund} /></div></div>
        <div className="card stat"><div className="label">İptal</div><div className="value"><Money value={s.totals.cancel} /></div></div>
      </div>
    </>
  );
}

/** 12 aylık net gider çubukları; çubuğa dokununca o ayın istatistiği açılır. */
function YearChart({ months, onPick }: { months: NonNullable<ExpenseStats['months']>; onPick: (ym: string) => void }) {
  const values = months.map((m) => Number(apiToKurus(m.net)));
  const max = Math.max(1, ...values);
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Aylara göre net gider</h2>
      <div className="month-bars" role="list">
        {months.map((m, i) => {
          const v = values[i] ?? 0;
          const h = v > 0 ? Math.max(4, Math.round((v / max) * 100)) : 0;
          return (
            <button key={m.year_month} role="listitem" className="month-bar" disabled={!m.status} onClick={() => onPick(m.year_month)}
              title={`${yearMonthLabel(m.year_month)}: ${m.status ? '' : 'kayıt yok'}`} aria-label={`${yearMonthLabel(m.year_month)} istatistiğini aç`}>
              <span className="bar-track"><span className="bar-fill" style={{ height: `${h}%` }} /></span>
              <span className="bar-label">{MONTHS_SHORT[i]}</span>
            </button>
          );
        })}
      </div>
      <div className="small muted">Bir aya dokunarak o ayın gider kalemlerini açın.</div>
    </div>
  );
}

function CategoryList({ s }: { s: ExpenseStats }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!s.categories.length) return <div className="card"><Empty>Bu dönemde gider kaydı yok.</Empty></div>;
  return (
    <div className="card">
      <div className="row"><h2 style={{ margin: 0 }}>Gider kalemleri</h2><span className="spacer" /><span className="small muted">Dokunun: fişleri görün</span></div>
      <ul className="list">
        {s.categories.map((c) => (
          <li key={c.key}>
            <button className="item cat-row" aria-expanded={open === c.key} onClick={() => setOpen(open === c.key ? null : c.key)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="title">{c.name}</div>
                <div className="share"><span style={{ width: `${Math.min(100, Math.max(0, c.share))}%` }} /></div>
                <div className="small muted">{c.count} fiş · %{c.share.toLocaleString('tr-TR')}</div>
              </div>
              <strong><Money value={c.net} /></strong>
              <span aria-hidden className="chev">{open === c.key ? '▴' : '▾'}</span>
            </button>
            {open === c.key && <CategoryReceipts c={c} s={s} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CategoryReceipts({ c, s }: { c: CategoryStat; s: ExpenseStats }) {
  const scope = s.scope === 'month' && s.year_month ? { year_month: s.year_month } : { year: s.year };
  const q = useQuery({ queryKey: ['stats-cat', c.key, scope], queryFn: () => ep.expenseCategory(c.key, scope) });
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <div style={{ padding: 8 }}><Loading lines={2} /></div>;
  const d = q.data;
  return (
    <div className="cat-detail">
      <div className="scroll-x">
        <table className="t">
          <thead><tr><th>Tarih</th><th>Satıcı / açıklama</th><th>Kişi</th><th>Durum</th><th style={{ textAlign: 'right' }}>Fiş tutarı</th><th style={{ textAlign: 'right' }}>Bu dönemdeki etkisi</th></tr></thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.receipt_id}>
                <td className="num">{formatDate(r.document_date)}</td>
                <td><Link to={`/fis/${r.receipt_id}`}>{r.vendor_name || r.description || 'Fiş'}</Link>
                  <div className="small muted">{r.category}{r.source_period ? ` · ${yearMonthLabel(r.source_period)} fişi` : ''}</div></td>
                <td>{r.owner_name}</td>
                <td><ReceiptBadge status={r.status} /></td>
                <td className="num" style={{ textAlign: 'right' }}><Money value={r.amount} /></td>
                <td className="num" style={{ textAlign: 'right' }}><Money value={r.effect.net} /></td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td colSpan={5}><strong>{d.name} toplamı</strong></td><td className="num" style={{ textAlign: 'right' }}><strong><Money value={d.totals.net} /></strong></td></tr></tfoot>
        </table>
      </div>
      <p className="small muted" style={{ margin: '6px 4px 0' }}>
        "Bu dönemdeki etkisi": fiş tutarından bu döneme yazılan iade ve iptaller düşülmüş haliyle. Önceki ayın fişine bu dönemde yapılan iade de burada görünür.
      </p>
    </div>
  );
}

/** Seçili ayın ya da yılın raporunu PDF olarak indirir (özet, kalem toplamları ve kalem bazında fiş dökümü). */
function PdfButton({ scope, label }: { scope: ep.StatsScope; label: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
      <button className="btn primary" disabled={busy} onClick={() => {
        setBusy(true); setErr(null);
        ep.downloadStatsPdf(scope).catch(setErr).finally(() => setBusy(false));
      }}>
        <Icon name="download" size={18} />{busy ? 'Hazırlanıyor…' : label}
      </button>
      <ErrorBox error={err} />
    </div>
  );
}
